import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useT } from '../i18n/i18n';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function PlannerDialog({ title, onClose, children, className }: { title: string; onClose: () => void; children: ReactNode; className?: string }) {
  const t = useT();
  const titleId = useId();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('h2')?.focus();
    return () => { if (before && document.contains(before)) before.focus(); };
  }, []);

  function onKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'Tab' && ref.current) {
      const list = ref.current.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (list.length === 0) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === ref.current.querySelector('h2'))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  return (
    <div className="modal-backdrop planner-dialog-backdrop">
      <div ref={ref} className={`modal planner-dialog ${className ?? ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} onKeyDown={onKey}>
        <div className="planner-dialog-head">
          <h2 id={titleId} className="modal-title" tabIndex={-1}>{title}</h2>
          <button type="button" className="btn ghost" onClick={onClose}>{t('Close')}</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ScopeDialog({ title, onChoose, onCancel, destructive }: { title: string; onChoose: (scope: 'ONE' | 'FOLLOWING' | 'ALL') => void; onCancel: () => void; destructive?: boolean }) {
  const t = useT();
  const cls = destructive ? 'btn danger' : 'btn';
  return (
    <PlannerDialog title={title} onClose={onCancel} className="planner-scope-dialog">
      <p className="modal-body">{t('This session is part of a repeating series.')}</p>
      <div className="planner-scope-choices">
        <button type="button" className={cls} onClick={() => onChoose('ONE')}>{destructive ? t('Delete only this session') : t('Change only this session')}</button>
        <button type="button" className={cls} onClick={() => onChoose('FOLLOWING')}>{t('This and following sessions')}</button>
        <button type="button" className={cls} onClick={() => onChoose('ALL')}>{t('All sessions in the series')}</button>
        <button type="button" className="btn secondary" onClick={onCancel}>{t('Cancel')}</button>
      </div>
    </PlannerDialog>
  );
}
